// Make sure to import Vec3 if it's not already
import { Events } from './events';
import { Splat } from './splat';
import { EnginePose } from './image-config-doc';
import { Quat, Vec3 } from 'playcanvas'; // Added Vec3

const registerSplatTransformHandler = (events: Events) => {
    // This event is fired by your Splat class whenever it's moved, rotated, or scaled
    const updatePoses = (splat: Splat) => {
        const originalPoses = events.invoke('poses.originalList') as EnginePose[];
        if (originalPoses.length === 0) {
            return; // No poses to transform
        }

        // Get the splat's current world transformation matrix
        const splatMatrix = splat.worldTransform;

        // --- Decompose the Matrix into its components ---
        // 1. Get world position (translation)
        const splatPosition = new Vec3();
        splatMatrix.getTranslation(splatPosition);

        // 2. Get world rotation (handles non-uniform scale correctly)
        const splatRotation = new Quat();
        splatRotation.setFromMat4(splatMatrix);

        // 3. Get world scale
        const splatScale = new Vec3();
        splatMatrix.getScale(splatScale);


        // Create a new list of transformed poses
        const transformedPoses = originalPoses.map(originalPose => {

            // --- Manual Position Transformation ---
            // This section replaces `splatMatrix.transformPoint(originalPose.position)`

            // Step 1: Scale the original pose's position by the splat's scale.
            // This is the Hadamard Product: S_splat ⊙ p_local
            const scaledPosition = new Vec3().mul2(splatScale, originalPose.position);

            // Step 2: Rotate the newly scaled vector by the splat's rotation.
            // This is the quaternion rotation: q_splat * v_scaled * q_splat^-1
            const rotatedPosition = splatRotation.transformVector(scaledPosition, scaledPosition);

            // Step 3: Translate by adding the splat's world position.
            // This is the final vector addition: P_splat + v_rotated
            const newPosition = new Vec3().add2(splatPosition, rotatedPosition);


            // --- Rotation Transformation (this is already the fundamental operation) ---
            // This correctly combines the two rotations: q_splat * q_local
            const newRotation = new Quat().mul2(splatRotation, originalPose.rotation);

            return {
                name: originalPose.name,
                position: newPosition,
                rotation: newRotation
            };
        });

        // Update the live pose data in the system
        events.invoke('poses.setTransformed', transformedPoses);
    };

    // This event is fired by your Splat class whenever it's moved, rotated, or scaled
    events.on('splat.moved', (splat: Splat) => {
        updatePoses(splat);
    });

    // When poses are loaded, we want to immediately transform them by the CURRENT splat transform
    events.on('poses.loaded', () => {
        // Try to find a target splat.
        // Option A: Use the currently selected splat
        let splat = events.invoke('selection') as Splat;

        // Option B: If nothing selected, just pick the first splat in the scene (if any)
        if (!splat || splat.type !== 'splat') {
            // We need a way to get *any* splat.
            // Since we don't have a direct 'splat.all' list exposed here easily without scene access,
            // we might rely on the user selecting one.
            // BUT, usually there is only one splat.
            // Let's try to get it from the scene if possible, or just wait for selection.
            // For now, let's assume the user has selected it or will select it.
            // Better yet, let's try to access the scene content if we can.
            // `events` doesn't give us scene directly.
            // However, `registerSplatTransformHandler` doesn't have `scene` passed in.
            // Let's just rely on selection for now, or maybe we can listen to 'splat.added' if we wanted.
        }

        if (splat && splat.type === 'splat') {
            updatePoses(splat);
        } else {
            // If no splat is selected, we can't transform relative to it yet.
            // The poses will remain as "original" (identity transform) until a splat is moved or selected.
            // Maybe we should trigger it when selection changes too?
        }
    });

    // Also update when selection changes, just in case we loaded poses while nothing was selected,
    // and then we select a splat.
    events.on('selection.changed', (selection: any) => {
        if (selection && selection.type === 'splat') {
            updatePoses(selection as Splat);
        }
    });
};

export { registerSplatTransformHandler };